import React from "react";

const About = () => {
  return (
    <div className="min-h-screen bg-slate-100 font-sans py-12 sm:py-16 lg:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section (Contained & Dark Theme to match Contact) */}
        <div className="relative bg-slate-900 text-white rounded-[2rem] shadow-2xl overflow-hidden py-20 lg:py-32 mb-16">
          {/* Background Image & Overlay */}
          <div className="absolute inset-0 z-0">
            <img
              src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?q=80&w=2948&auto=format&fit=crop"
              alt="Clouds from airplane window"
              className="w-full h-full object-cover opacity-20 mix-blend-luminosity"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 to-slate-900"></div>
          </div>

          {/* Blurred Decorative Orbs */}
          <div className="absolute top-0 left-0 -ml-20 -mt-20 w-80 h-80 rounded-full bg-blue-500/20 blur-3xl pointer-events-none"></div>
          <div className="absolute bottom-0 right-0 -mr-20 -mb-20 w-80 h-80 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none"></div>

          {/* Hero Content */}
          <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6">
              Redefining the way you{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-200">
                travel.
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 font-medium leading-relaxed">
              At TripReserver, we believe that exploring the world shouldn't be
              complicated or overly expensive. We're on a mission to make flight
              booking transparent, accessible, and effortless.
            </p>
          </div>
        </div>

        {/* Floating Stats Row */}
        <div className="relative z-20 max-w-5xl mx-auto -mt-28 mb-20 px-4 sm:px-6">
          <div className="bg-white rounded-[2rem] shadow-xl shadow-slate-200/50 border border-slate-100 p-8 sm:p-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center divide-x divide-slate-100">
              <div>
                <div className="text-3xl sm:text-4xl font-black text-slate-900 mb-2">
                  40M+
                </div>
                <div className="text-xs sm:text-sm font-bold text-slate-400 uppercase tracking-wider">
                  Travelers
                </div>
              </div>
              <div>
                <div className="text-3xl sm:text-4xl font-black text-slate-900 mb-2">
                  20+
                </div>
                <div className="text-xs sm:text-sm font-bold text-slate-400 uppercase tracking-wider">
                  Years Exp.
                </div>
              </div>
              <div>
                <div className="text-3xl sm:text-4xl font-black text-slate-900 mb-2">
                  24/7
                </div>
                <div className="text-xs sm:text-sm font-bold text-slate-400 uppercase tracking-wider">
                  Support
                </div>
              </div>
              <div>
                <div className="text-3xl sm:text-4xl font-black text-slate-900 mb-2">
                  1M+
                </div>
                <div className="text-xs sm:text-sm font-bold text-slate-400 uppercase tracking-wider">
                  Partners
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Our Values Section */}
        <div className="text-center mb-12 max-w-2xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mb-4 tracking-tight">
            Why choose TripReserver?
          </h2>
          <p className="text-slate-500 text-lg font-medium leading-relaxed">
            We combine cutting-edge technology with real human expertise to
            deliver an unmatched travel booking experience.
          </p>
        </div>

        {/* Values Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {/* Trust Card */}
          <div className="bg-white rounded-[2rem] p-8 lg:p-10 border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
            <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mb-6 group-hover:bg-blue-600 group-hover:text-white transition-colors">
              <svg
                className="w-7 h-7"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3">
              Trust & Transparency
            </h3>
            <p className="text-slate-500 leading-relaxed">
              No hidden fees, no surprise charges. The price you see is the
              price you pay, completely upfront.
            </p>
          </div>

          {/* Technology Card */}
          <div className="bg-white rounded-[2rem] p-8 lg:p-10 border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
            <div className="w-14 h-14 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mb-6 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
              <svg
                className="w-7 h-7"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3">
              Smart Technology
            </h3>
            <p className="text-slate-500 leading-relaxed">
              Our algorithms search thousands of routes in seconds to find you
              the absolute best deals available.
            </p>
          </div>

          {/* Support Card */}
          <div className="bg-white rounded-[2rem] p-8 lg:p-10 border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
            <div className="w-14 h-14 bg-orange-50 text-orange-500 rounded-full flex items-center justify-center mb-6 group-hover:bg-orange-500 group-hover:text-white transition-colors">
              <svg
                className="w-7 h-7"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-3">
              Human Support
            </h3>
            <p className="text-slate-500 leading-relaxed">
              Technology is great, but sometimes you just need to talk to a
              person. Our experts are here 24/7.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
